// EC-849-Create a custom redux store to store the data
import isApiError from "@insite/client-framework/Common/isApiError";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import {
    getSelectedCategoryPath,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";

import {
    GetProductCollectionApiV2Parameter,
    getProductCollectionRealTimePrices,
    getProductCollectionV2,
} from "@WhiteCap/Services/ProductGroupingLoadProductService";

type HandlerType = Handler<GetProductCollectionApiV2Parameter, any>;

export const DispatchBeginLoadProducts: HandlerType = props => {
    props.dispatch({
        type: "Custom/GroupingProducts/BeginLoadProducts",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    const { onSuccess, onError, isLoadMore, ...parameter } = props.parameter;

    props.apiParameter = parameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    const state = props.getState();
    const currentPage = getCurrentPage(state);
    const { storefrontAccess } = getSettingsCollection(state).productSettings;
    if (
        storefrontAccess === StorefrontAccess.SignInRequiredToBrowse &&
        !state.context.session.isAuthenticated &&
        (currentPage.fields.excludeFromSignInRequired || currentPage.type === "SignInPage")
    ) {
        return false;
    }
    try {
        props.apiParameter.search = "SET_SEARCH_KEY_CATALOG_VIEW";
        props.apiResult = await getProductCollectionV2(props.apiParameter);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }

        if (storefrontAccess === StorefrontAccess.SignInRequiredToBrowse && error.status === 403) {
            // auth timed out with SignInRequiredToBrowsee - throw this up as a 401 so DisplayError reloads the site
            error.status = 401;
            error.errorJson = { message: "error" };
        }

        throw error;
    }
};

export const GetRealTimePricing: HandlerType = async props => {
    const state = props.getState();
    const session = getSession(state);
    if (!session.isAuthenticated) {
        return;
    }
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const categoryPath =catalogPage?.canonicalPath;
    const products = props.apiResult.products;
    if (products && products.length) {
        const productPriceParameters = products.map((product: ProductModel) => {
            return {
                productDetailPath: `${categoryPath}/${product.urlSegment}`,
                productId: product.id,
                qtyOrdered: 1,
                unitOfMeasure: "",
            };
        });

        const pricing = await getProductCollectionRealTimePrices(productPriceParameters);

        let productWisePricing: { [key: string]: ProductPriceDto } = {};

        // Function to add key-value pairs
        function addKeyValuePair(key: string, value: ProductPriceDto) {
            if (productWisePricing) productWisePricing[key] = value;
        }

        pricing.realTimePricingResults?.forEach(price => {
            addKeyValuePair(price.productId, price);
        });

        props.dispatch({
            type: "Custom/GroupingProducts/ProductsPrice",
            result: productWisePricing,
        });
    }
};

export const DispatchCompleteLoadProducts: HandlerType = props => {
    const { onSuccess, onError, isLoadMore, ...parameter } = props.parameter;
    props.apiParameter = parameter;
    props.dispatch({
        type: "Custom/GroupingProducts/CompleteLoadProducts",
        result: props.apiResult,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.(props.apiResult.products!);
};

export const chain = [
    DispatchBeginLoadProducts,
    PopulateApiParameter,
    RequestDataFromApi,
    GetRealTimePricing,
    DispatchCompleteLoadProducts,
    ExecuteOnSuccessCallback,
];

const productGroupingLoadProducts = createHandlerChainRunner(chain, "ProductGroupingLoadProducts");
export default productGroupingLoadProducts;
